import { useState, useEffect } from 'react'
import { Clock, ChevronLeft, ChevronRight, Filter, X, Sparkles, Calendar, LogIn, LogOut, Timer, MapPin, Building2, Info, Inbox, TrendingUp } from 'lucide-react'
import { attendanceAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { formatDate, formatTime, todayISO } from '../../lib/dateFormat'

const STATUS_STYLES: Record<string, { chip: string; dot: string; bar: string }> = {
  PRESENT: { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
  HALF_DAY: { chip: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'bg-amber-500', bar: 'bg-amber-500' },
  ABSENT: { chip: 'bg-red-50 text-red-700 ring-red-200', dot: 'bg-red-500', bar: 'bg-red-500' },
  LATE: { chip: 'bg-orange-50 text-orange-700 ring-orange-200', dot: 'bg-orange-500', bar: 'bg-orange-500' },
  ON_LEAVE: { chip: 'bg-blue-50 text-blue-700 ring-blue-200', dot: 'bg-blue-500', bar: 'bg-blue-500' },
}

const STATUS_LABELS: Record<string, string> = {
  PRESENT: 'Present',
  HALF_DAY: 'Half Day',
  ABSENT: 'Absent',
  LATE: 'Late',
  ON_LEAVE: 'On Leave',
}

const DEFAULT_STATUS = { chip: 'bg-neutral-100 text-neutral-700 ring-neutral-200', dot: 'bg-neutral-400', bar: 'bg-neutral-400' }

function daysAgoISO(days: number) {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}
function formatDuration(decimalHours: number | string | null | undefined) {
  if (decimalHours === null || decimalHours === undefined || decimalHours === '') {
    return '—'
  }

  const hours = Number(decimalHours)

  if (Number.isNaN(hours)) {
    return '—'
  }

  const totalMinutes = Math.round(hours * 60)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60

  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`

  return `${h}h ${m}m`
}
export default function AttendanceHistoryPage() {
  const { error: toastError } = useToast()
  const [records, setRecords] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [shiftConfig, setShiftConfig] = useState<any>(null)
  const [showFilters, setShowFilters] = useState(false)

  const [dateFrom, setDateFrom] = useState(daysAgoISO(30))
  const [dateTo, setDateTo] = useState(todayISO())
  const [statusFilter, setStatusFilter] = useState('')

  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)
  const PAGE_SIZE = 20

  const load = async () => {
    setIsLoading(true)
    try {
      const filters: any = { from: dateFrom, to: dateTo }
      if (statusFilter) filters.status = statusFilter
      const res = await attendanceAPI.getHistory(page, filters)
      const data = res.data
      if (Array.isArray(data)) {
        setRecords(data)
        setCount(data.length)
        setHasNext(false)
        setHasPrev(false)
      } else {
        setRecords(data.results || [])
        setCount(data.count ?? 0)
        setHasNext(!!data.next)
        setHasPrev(!!data.previous)
      }
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load attendance history.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dateFrom, dateTo, statusFilter])

  useEffect(() => {
    attendanceAPI.getShiftConfig().then((res) => setShiftConfig(res.data)).catch(() => { })
  }, [])

  const updateFilter = (setter: (v: string) => void, value: string) => {
    setter(value)
    setPage(1)
  }

  const applyQuickRange = (days: number) => {
    updateFilter(setDateFrom, daysAgoISO(days))
    setDateTo(todayISO())
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE))

  const hasActiveFilters = Boolean(statusFilter)

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            My Records
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Attendance History
          </h1>
          <p className="text-sm text-neutral-500">Your attendance records and check-in/out logs</p>
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`group flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5 self-start ${showFilters || hasActiveFilters
              ? 'border border-[#E4670F]/40 bg-[#E4670F]/10 text-[#C24A0A] hover:bg-[#E4670F]/15'
              : 'border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
            }`}
        >
          <Filter className="w-4 h-4 transition-transform group-hover:scale-110" />
          Filters
          {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-[#E4670F]" />}
        </button>
      </div>

      {/* ── Shift config banner ── */}
      {shiftConfig && (
        <div className="mb-6 flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">
          <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            Shift starts at <strong className="font-semibold">{shiftConfig.shift_start_time?.slice(0, 5)}</strong> with a{' '}
            <strong className="font-semibold">{shiftConfig.grace_period_minutes}-minute</strong> grace period.
            Checking in after that marks you <strong className="font-semibold">LATE</strong> for the day.
          </p>
        </div>
      )}

      {/* ── Filters panel ── */}
      {showFilters && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center">
                <Filter className="w-3.5 h-3.5 text-white" />
              </div>
              <h2 className="text-sm font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                Quick Ranges & Filters
              </h2>
            </div>
            <button
              onClick={() => setShowFilters(false)}
              className="p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Quick ranges */}
          <div className="flex flex-wrap gap-2 mb-4">
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                onClick={() => applyQuickRange(d)}
                className="px-3.5 py-1.5 text-xs font-medium border border-neutral-200 bg-white rounded-full text-neutral-700 hover:border-[#E4670F]/40 hover:bg-[#E4670F]/[0.06] hover:text-[#C24A0A] transition-all"
              >
                Last {d === 365 ? '1 year' : `${d} days`}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">From</label>
              <div className="relative group">
                <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <input
                  type="date" value={dateFrom} max={dateTo}
                  onChange={(e) => updateFilter(setDateFrom, e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">To</label>
              <div className="relative group">
                <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <input
                  type="date" value={dateTo} min={dateFrom} max={todayISO()}
                  onChange={(e) => updateFilter(setDateTo, e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Status</label>
              <div className="relative group">
                <TrendingUp className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <select
                  value={statusFilter}
                  onChange={(e) => updateFilter(setStatusFilter, e.target.value)}
                  className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
                >
                  <option value="">All Statuses</option>
                  {Object.keys(STATUS_STYLES).map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Results strip ── */}
      {!isLoading && records.length > 0 && (
        <div className="flex items-center gap-2 mb-4 text-sm text-neutral-600">
          <TrendingUp className="w-4 h-4 text-neutral-400" />
          <span>
            <span className="font-semibold text-neutral-900 tabular-nums">{count}</span>{' '}
            {count === 1 ? 'record' : 'records'}
          </span>
        </div>
      )}

      {/* ── Records ── */}
      {isLoading ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
          <p className="text-sm text-neutral-500">Loading attendance history...</p>
        </div>
      ) : records.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center">
            <Inbox className="w-8 h-8 text-neutral-400" />
          </div>
          <p className="text-sm font-medium text-neutral-700">No attendance records found</p>
          <p className="text-xs text-neutral-400">Try widening the date range or clearing the status filter.</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {records.map((record) => {
              const style = STATUS_STYLES[record.status] || DEFAULT_STATUS
              const label = STATUS_LABELS[record.status] || record.status
              return (
                <div
                  key={record.id}
                  className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden hover:shadow-lg hover:shadow-neutral-200/50 hover:ring-neutral-300/70 transition-all duration-300"
                >
                  {/* Left status accent bar */}
                  <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.bar}`} />

                  <div className="p-5 pl-6">
                    {/* Top row — date + status */}
                    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-sm flex-shrink-0">
                          <Calendar className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-neutral-900 truncate">
                            {formatDate(record.date)}
                          </p>
                          <p className="text-xs text-neutral-500 inline-flex items-center gap-1 tabular-nums">
                            <LogIn className="w-3 h-3 text-emerald-500" />
                            {formatTime(record.check_in_time)}
                            <span className="text-neutral-300 mx-1">→</span>
                            <LogOut className="w-3 h-3 text-red-500" />
                            {formatTime(record.check_out_time)}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 whitespace-nowrap ${style.chip}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        {label}
                      </span>
                    </div>

                    {/* Metrics grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-blue-50/70 border border-blue-100 rounded-xl px-3.5 py-2.5">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Timer className="w-3 h-3 text-blue-600" />
                          <p className="text-[10px] uppercase tracking-wide font-semibold text-blue-700">
                            Total Hours
                          </p>
                        </div>
                        <p className="text-sm font-bold text-neutral-900 tabular-nums leading-tight">
                          {formatDuration(record.total_hours)}
                        </p>
                      </div>

                      <div className="bg-emerald-50/70 border border-emerald-100 rounded-xl px-3.5 py-2.5">
                        <div className="flex items-center gap-1.5 mb-1">
                          <MapPin className="w-3 h-3 text-emerald-600" />
                          <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">
                            Check-in
                          </p>
                        </div>
                        <p className="text-xs font-semibold text-neutral-700 tabular-nums leading-tight truncate">
                          {record.check_in_lat
                            ? `${Number(record.check_in_lat).toFixed(3)}, ${Number(record.check_in_lng).toFixed(3)}`
                            : '—'}
                        </p>
                      </div>

                      <div className="bg-red-50/70 border border-red-100 rounded-xl px-3.5 py-2.5">
                        <div className="flex items-center gap-1.5 mb-1">
                          <MapPin className="w-3 h-3 text-red-600" />
                          <p className="text-[10px] uppercase tracking-wide font-semibold text-red-700">
                            Check-out
                          </p>
                        </div>
                        <p className="text-xs font-semibold text-neutral-700 tabular-nums leading-tight truncate">
                          {record.check_out_lat
                            ? `${Number(record.check_out_lat).toFixed(3)}, ${Number(record.check_out_lng).toFixed(3)}`
                            : '—'}
                        </p>
                      </div>

                      <div className="bg-violet-50/70 border border-violet-100 rounded-xl px-3.5 py-2.5">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Building2 className="w-3 h-3 text-violet-600" />
                          <p className="text-[10px] uppercase tracking-wide font-semibold text-violet-700">
                            Centre
                          </p>
                        </div>
                        <p className="text-xs font-semibold text-neutral-700 leading-tight truncate" title={record.training_centre_name || ''}>
                          {record.training_centre_name || '—'}
                          {record.training_centre_code && (
                            <span className="text-neutral-400 font-normal"> ({record.training_centre_code})</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Admin remarks */}
                    {record.admin_remarks && (
                      <div className="mt-4 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5">
                        <Info className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 mb-0.5">
                            Admin Note
                          </p>
                          <p className="text-xs text-amber-800 leading-relaxed">{record.admin_remarks}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* ── Pagination ── */}
          {(hasNext || hasPrev || totalPages > 1) && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 bg-white rounded-2xl ring-1 ring-neutral-200/70 px-5 py-3.5 shadow-sm">
              <p className="text-sm text-neutral-600">
                Page <span className="font-semibold text-neutral-900 tabular-nums">{page}</span> of{' '}
                <span className="font-semibold text-neutral-900 tabular-nums">{totalPages}</span>
                <span className="text-neutral-300 mx-2">·</span>
                <span className="tabular-nums">{count}</span> {count === 1 ? 'record' : 'records'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!hasPrev && page <= 1}
                  className="group inline-flex items-center gap-1.5 px-3.5 py-2 border border-neutral-200 bg-white rounded-xl text-sm font-medium text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                >
                  <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5 group-disabled:translate-x-0" />
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasNext}
                  className="group inline-flex items-center gap-1.5 px-3.5 py-2 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:from-[#A31621] disabled:hover:to-[#7A1F1F] transition-all shadow-md shadow-[#A31621]/15 hover:shadow-lg"
                >
                  Next
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-disabled:translate-x-0" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}